import { Plane, Heart, Utensils, Bell, Home, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

type Persona = "travel" | "health" | "meals" | "errands" | "home" | "work";

interface AppSidebarProps {
  selectedPersona: Persona | null;
  onPersonaSelect: (persona: Persona) => void;
  onShowAllMemories: () => void;
  showAllMemories: boolean;
  onReturnToChat: () => void;
}

const personaItems = [
  { 
    type: "travel" as Persona, 
    title: "Travel", 
    description: "Flights, hotels, itineraries", 
    icon: Plane,
    color: "bg-gradient-to-r from-primary to-accent",
    hoverColor: "hover:bg-gradient-to-r hover:from-primary/80 hover:to-accent/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
  { 
    type: "health" as Persona, 
    title: "Health", 
    description: "Fitness, sleep, wellness", 
    icon: Heart,
    color: "bg-gradient-to-r from-accent to-primary",
    hoverColor: "hover:bg-gradient-to-r hover:from-accent/80 hover:to-primary/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
  { 
    type: "meals" as Persona, 
    title: "Meals", 
    description: "Meal planning, nutrition", 
    icon: Utensils,
    color: "bg-gradient-to-r from-primary to-accent",
    hoverColor: "hover:bg-gradient-to-r hover:from-primary/80 hover:to-accent/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
  { 
    type: "errands" as Persona, 
    title: "Errands", 
    description: "Appointments, calls, renewals", 
    icon: Bell,
    color: "bg-gradient-to-r from-accent to-primary",
    hoverColor: "hover:bg-gradient-to-r hover:from-accent/80 hover:to-primary/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
  { 
    type: "home" as Persona, 
    title: "Home", 
    description: "Bills, maintenance", 
    icon: Home,
    color: "bg-gradient-to-r from-primary to-accent",
    hoverColor: "hover:bg-gradient-to-r hover:from-primary/80 hover:to-accent/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
  { 
    type: "work" as Persona, 
    title: "Work", 
    description: "Tasks, meetings, projects", 
    icon: Briefcase,
    color: "bg-gradient-to-r from-accent to-primary",
    hoverColor: "hover:bg-gradient-to-r hover:from-accent/80 hover:to-primary/80",
    textColor: "text-secondary",
    selectedTextColor: "text-white"
  },
];

export function AppSidebar({ selectedPersona, onPersonaSelect, onShowAllMemories, showAllMemories, onReturnToChat }: AppSidebarProps) {
  const { state, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";
  const isMobile = useIsMobile();

  const handleLogoClick = () => {
    onReturnToChat();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handlePersonaSelect = (persona: Persona) => {
    onPersonaSelect(persona);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleShowAllMemories = () => {
    onShowAllMemories();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar className="border-r bg-graphite shadow-lg border-secondary/30">
      <SidebarHeader className="p-0 bg-graphite text-white flex-shrink-0">
        <div className="flex flex-col items-center py-2">
          <button onClick={handleLogoClick} className="cursor-pointer">
            <img 
              src="/lovable-uploads/09bad5d5-844f-45b8-a99a-cd62d8b8f3f0.png"
              alt="Shared Matter Logo"
              className="w-16 h-16 object-contain hover:opacity-80 transition-opacity"
            />
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent className="flex-1 bg-graphite overflow-y-auto">
        <SidebarGroup className="py-2">
          <SidebarGroupLabel className="text-accent font-bold text-sm py-2 tracking-wider uppercase">Categories</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {personaItems.map((item) => (
                <SidebarMenuItem key={item.type}>
                  <SidebarMenuButton
                    onClick={() => handlePersonaSelect(item.type)}
                    isActive={selectedPersona === item.type && !showAllMemories}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 border border-transparent font-medium min-h-[52px] ${
                      selectedPersona === item.type && !showAllMemories
                        ? `${item.color} ${item.selectedTextColor} shadow-lg scale-105 border-white/30` 
                        : `hover:bg-secondary/20 ${item.hoverColor} ${item.textColor} hover:scale-102 hover:shadow-md text-secondary`
                    }`}
                  >
                    <item.icon className={`h-5 w-5 flex-shrink-0 ${selectedPersona === item.type && !showAllMemories ? 'animate-pulse' : ''}`} />
                    {!isCollapsed && (
                      <div className="flex flex-col items-start leading-relaxed min-w-0 flex-1">
                        <span className="font-semibold text-sm leading-normal">{item.title}</span>
                        <span className={`text-xs leading-normal mt-1.5 ${selectedPersona === item.type && !showAllMemories ? 'text-white/85' : 'text-secondary/75'}`}>
                          {item.description}
                        </span>
                      </div>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="bg-gradient-to-r from-transparent via-secondary/50 to-transparent my-3" />

        <SidebarGroup className="py-2">
          <SidebarGroupLabel className="text-accent font-bold text-sm py-2 tracking-wider uppercase">Tools</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  onClick={handleShowAllMemories} 
                  isActive={showAllMemories}
                  className={`flex items-center gap-3 rounded-lg transition-all duration-200 hover:scale-102 font-medium p-3 min-h-[42px] ${
                    showAllMemories 
                      ? 'bg-gradient-to-r from-primary to-accent text-white shadow-lg scale-105 border border-white/30' 
                      : 'hover:bg-secondary/20 text-secondary hover:text-white'
                  }`}
                >
                  {!isCollapsed && <span className="text-sm leading-tight">📚 All Memories</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link 
                    to="/calendar" 
                    className="flex items-center gap-3 hover:bg-secondary/20 text-secondary hover:text-white rounded-lg transition-all duration-200 hover:scale-102 font-medium p-3 min-h-[42px]"
                    onClick={() => {
                      if (isMobile) {
                        setOpenMobile(false);
                      }
                    }}
                  >
                    {!isCollapsed && <span className="text-sm leading-tight">📅 Calendar</span>}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link 
                    to="/slack" 
                    className="flex items-center gap-3 hover:bg-secondary/20 text-secondary hover:text-white rounded-lg transition-all duration-200 hover:scale-102 font-medium p-3 min-h-[42px]"
                    onClick={() => {
                      if (isMobile) {
                        setOpenMobile(false);
                      }
                    }}
                  >
                    {!isCollapsed && <span className="text-sm leading-tight">💬 Slack</span>}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2 bg-graphite flex-shrink-0">
        {/* Footer content removed - user profile moved to header */}
      </SidebarFooter>
    </Sidebar>
  );
}
